import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';

dayjs.extend(relativeTime);
dayjs.extend(utc);

/**
 * Format a UTC timestamp as `MMM D, YYYY h:mm A UTC` (e.g. "May 16, 2026 2:30 PM
 * UTC"). The Playlists report is UTC-bucketed, so exact switch times are shown
 * in UTC to stay consistent with the report's date grain. Empty string when
 * missing/invalid.
 */
export function formatUtcDateTime(value: string | Date | number | null | undefined): string {
  if (!value) {
    return '';
  }
  const parsed = dayjs.utc(value);
  return parsed.isValid() ? parsed.format('MMM D, YYYY h:mm A [UTC]') : '';
}

export enum DateFormat {
  STANDARD_DATE = 'YYYY-MM-DD',
  STANDARD_DATETIME = 'YYYY-MM-DD HH:mm:ss',
  DATETIME_12HOUR = 'YYYY-MM-DD h:mm:ss A',
  TIME_12HOUR = 'h:mm:ss A',
  DATE_HUMAN = 'M/D/YY',
  DATETIME_HUMAN = 'M/D/YY h:mm A',
  /** Two-digit mm/dd/yy + 12h clock — Audio File Issues list + inspector. */
  US_MM_DD_YY_TIME_12H = 'MM/DD/YY h:mm A',
  /** Two-digit mm/dd/yy, date only — compact card metadata rows (time lives in the tooltip). */
  US_MM_DD_YY = 'MM/DD/YY'
}

export const isDate = (value: unknown): boolean => {
  if (typeof value === 'boolean') {
    return false;
  }

  if (dayjs.isDayjs(value)) {
    return true;
  }

  if (value instanceof Date) {
    return !isNaN(value.getTime());
  }

  try {
    const parsed = dayjs(value as string | number);
    return parsed.isValid();
  } catch {
    return false;
  }
};

export const humanReadableDate = (
  date: string | Date | null | number | undefined,
  format: string = DateFormat.STANDARD_DATE
): string => {
  if (!date) {
    return '';
  }

  return dayjs(date).format(format);
};

export const timeFromNow = (date: string): string => {
  return dayjs(date).fromNow();
};

/**
 * US-style `mm/dd/yy h:mm AM/PM` (padded month/day). Empty string when missing/invalid.
 */
export function formatUsMmDdYyTime12h(
  value: string | Date | number | null | undefined
): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const d = dayjs(value);

  return d.isValid() ? d.format(DateFormat.US_MM_DD_YY_TIME_12H) : '';
}

/**
 * US-style `mm/dd/yy` date only (padded month/day). Empty string when missing/invalid.
 */
export function formatUsMmDdYy(
  value: string | Date | number | null | undefined
): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const d = dayjs(value);

  return d.isValid() ? d.format(DateFormat.US_MM_DD_YY) : '';
}

/** What a date pair says when its end is before its start. */
export const END_BEFORE_START_MESSAGE = "The end date can't be before the start date.";

/**
 * Whether an end date falls before its start date, for two calendar dates
 * written as YYYY-MM-DD. That form sorts as text in date order, so
 * `'2026-11-01' < '2026-11-07'` compares the days. The same day at both ends
 * is not before. A missing end or start leaves nothing to compare.
 */
export function isEndBeforeStart(start: string | null | undefined, end: string | null | undefined): boolean {
  return Boolean(start) && Boolean(end) && end! < start!;
}
